import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useMemo, useState } from 'react';
import useWorkspacesEffects from '../../hooks/context/effects/useWorkspacesEffects';
import useWorkspacesContext from '../../hooks/context/useWorkspacesContext';
import { Dashboard, Workspace, WorkspaceScore, WorkspaceUserMembership } from '../../types/files';
import storage from '../../utils/storage';
import {AuthContext} from '../AuthContext';
import useWorkspaces from '../../hooks/useWorkspaces';

type WorkspacesContextProps = {
  workspacesLoaded: boolean;
  workspaces: Workspace[];
  setWorkspaces: Dispatch<SetStateAction<Workspace[]>>;
  workspacesDashboards: Record<string, Dashboard[]>;
  setWorkspacesDashboards: Dispatch<SetStateAction<Record<string, Dashboard[]>>>;
  workspacesCollection: Record<string, Workspace[]>;
  setWorkspacesCollection: Dispatch<SetStateAction<Record<string, Workspace[]>>>;
  acceptedWorkspaces: Workspace[];
  pendingWorkspaces: Workspace[];
  acceptedInvestors: WorkspaceUserMembership[];
  pendingInvestors: WorkspaceUserMembership[];
  workspaceScores: WorkspaceScore[];
  setWorkspaceScores: Dispatch<SetStateAction<WorkspaceScore[]>>;
  matchedAssociatedDashboards: Record<string, Dashboard[]>;
  setMatchedAssociatedDashboards: Dispatch<SetStateAction<Record<string, Dashboard[]>>>;
};

export const WorkspacesContext = createContext<WorkspacesContextProps>({
  workspacesLoaded: false,
  workspaces: [],
  setWorkspaces: () => {},
  workspacesDashboards: {},
  setWorkspacesDashboards: () => {},
  workspacesCollection: {},
  setWorkspacesCollection: () => {},
  acceptedWorkspaces: [],
  pendingWorkspaces: [],
  acceptedInvestors: [],
  pendingInvestors: [],
  workspaceScores: [],
  setWorkspaceScores: () => {},
  matchedAssociatedDashboards: {},
  setMatchedAssociatedDashboards: () => {},
});

const WorkspacesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userGroup } = useContext(AuthContext);
  const {
    workspaces,
    workspacesDashboards,
    workspacesCollection,
    workspaceScores,
    setWorkspaces,
    setWorkspacesDashboards,
    setWorkspacesCollection,
    setWorkspaceScores
  } = useWorkspacesContext();
  const {
    acceptedWorkspaces,
    pendingWorkspaces,
    acceptedInvestors,
    pendingInvestors
  } = useWorkspaces();
  const { workspacesLoaded } = useWorkspacesEffects(userGroup);
  const [matchedAssociatedDashboards, setMatchedAssociatedDashboards] = useState<Record<string, Dashboard[]>>({});

  const contextValue = useMemo(() => ({
    workspacesLoaded,
    workspaces,
    setWorkspaces,
    workspacesDashboards,
    setWorkspacesDashboards,
    workspacesCollection,
    setWorkspacesCollection,
    acceptedWorkspaces,
    pendingWorkspaces,
    acceptedInvestors,
    pendingInvestors,
    workspaceScores,
    setWorkspaceScores,
    matchedAssociatedDashboards,
    setMatchedAssociatedDashboards,
  }), [
    workspacesLoaded,
    workspaces,
    setWorkspaces,
    workspacesDashboards,
    setWorkspacesDashboards,
    workspacesCollection,
    setWorkspacesCollection,
    acceptedWorkspaces,
    pendingWorkspaces,
    acceptedInvestors,
    pendingInvestors,
    workspaceScores,
    setWorkspaceScores,
    matchedAssociatedDashboards,
    setMatchedAssociatedDashboards,
  ]);

  if (storage.getItem('config.logger-context'))
    console.log("CTX", ['Workspaces', contextValue]);

  return (
    <WorkspacesContext.Provider value={contextValue}>
      {children}
    </WorkspacesContext.Provider>
  );
};

export default WorkspacesProvider;